import * as BABYLON from '@babylonjs/core';
import { DungeonEvents } from '../../types/game-events';
import { EventsManager } from '@game-libs/events-manager';

export interface ExitDoorConfig {
  position: BABYLON.Vector3;
  rotation: number;
  cellSize: number;
  wallHeight: number;
  scene: BABYLON.Scene;
  camera: BABYLON.Camera;
  hasGoldenKey: () => boolean;
  tileRow: number;
  tileCol: number;
  onEnterTile?: () => void;
  showDebug?: boolean;
}

const ASSETS_BASE_PATH = 'dungeon-crawler/tiles';

export class DungeonExitDoor {
  private mesh: BABYLON.Mesh;
  private collisionBox: BABYLON.Mesh;
  private isOpen: boolean = false;
  private position: BABYLON.Vector3;
  private rotation: number;
  private scene: BABYLON.Scene;
  private camera: BABYLON.Camera;
  private showDebug: boolean;
  private pointerObserver: BABYLON.Nullable<BABYLON.Observer<BABYLON.PointerInfo>> = null;
  private _disposed = false;
  private closedMaterial: BABYLON.StandardMaterial;
  private openMaterial: BABYLON.StandardMaterial;
  private hasGoldenKey: () => boolean;
  private onEnterTile?: () => void;
  private cellSize: number;
  private hasTriggeredEntry: boolean = false;
  private tileRow: number;
  private tileCol: number;
  private eventsManager = EventsManager();

  constructor(config: ExitDoorConfig) {
    this.position = config.position;
    this.rotation = config.rotation;
    this.scene = config.scene;
    this.camera = config.camera;
    this.showDebug = config.showDebug || false;
    this.hasGoldenKey = config.hasGoldenKey;
    this.onEnterTile = config.onEnterTile;
    this.cellSize = config.cellSize;
    this.tileRow = config.tileRow;
    this.tileCol = config.tileCol;

    // Create materials
    const createMaterial = (name: string, url: string) => {
      const mat = new BABYLON.StandardMaterial(name, config.scene);
      const tex = new BABYLON.Texture(url, config.scene, false, true, BABYLON.Texture.NEAREST_SAMPLINGMODE);
      mat.diffuseTexture = tex;
      tex.hasAlpha = true;
      mat.useAlphaFromDiffuseTexture = true;
      mat.transparencyMode = BABYLON.Material.MATERIAL_ALPHATEST;
      mat.alphaCutOff = 0.5;
      mat.backFaceCulling = false;
      mat.emissiveColor = new BABYLON.Color3(0.7, 0.7, 0.7);
      mat.specularColor = BABYLON.Color3.Black();
      return mat;
    };

    this.closedMaterial = createMaterial('exitMat', `${ASSETS_BASE_PATH}/exit.png`);
    this.openMaterial = createMaterial('exitOpenMat', `${ASSETS_BASE_PATH}/exit_open.png`);

    // Create the visual exit door mesh
    this.mesh = BABYLON.MeshBuilder.CreateGround(
      `exitDoor_${this.position.x}_${this.position.z}`,
      { width: config.cellSize, height: config.cellSize },
      config.scene
    );
    this.mesh.rotation.x = -Math.PI / 2; // Make it vertical
    this.mesh.rotation.y = this.rotation;
    this.mesh.material = this.closedMaterial;
    this.mesh.position = this.position.clone();
    this.mesh.renderingGroupId = 0;
    this.mesh.isPickable = true; // Make it pickable for clicks
    this.mesh.material.backFaceCulling = false; // Visible from both sides
    this.mesh.cullingStrategy = BABYLON.Mesh.OCCLUSION_TYPE_OPTIMISTIC;

    // Create a THIN collision box at the door position that only blocks when closed
    // The collision should be a thin plane perpendicular to the door
    const collisionThickness = 0.2; // Very thin to not block the spawn
    const collisionWidth = config.cellSize;
    const collisionHeight = config.wallHeight;

    // Normalize rotation to 0-2π range
    const normalizedRotation = ((this.rotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    // Determine if door is horizontal (facing +Z or -Z) or vertical (facing +X or -X)
    // Horizontal doors (0° or 180°) need thin width, full depth
    // Vertical doors (90° or 270°) need full width, thin depth
    const isHorizontal = Math.abs(Math.sin(normalizedRotation)) < 0.5;

    this.collisionBox = BABYLON.MeshBuilder.CreateBox(
      `exitDoorCollision_${this.position.x}_${this.position.z}`,
      {
        width: isHorizontal ? collisionThickness : collisionWidth,
        height: collisionHeight,
        depth: isHorizontal ? collisionWidth : collisionThickness
      },
      config.scene
    );
    this.collisionBox.position = this.position.clone();

    // Rotate collision box to match door orientation
    // For top/bottom exits, add 90 degrees to match the visual door rotation
    let collisionRotation = this.rotation;
    if (isHorizontal) {
      collisionRotation += Math.PI / 2; // Add 90 degrees for vertical exits (top/bottom)
    }
    this.collisionBox.rotation.y = collisionRotation;

    this.collisionBox.checkCollisions = true;
    this.collisionBox.isVisible = this.showDebug;
    this.collisionBox.isPickable = true; // Must be true so MazeCameraController rays hit it and block movement

    // Debug: Make collision box semi-transparent blue when visible
    if (this.showDebug) {
      const debugMat = new BABYLON.StandardMaterial(`exitDoorDebugMat_${this.position.x}_${this.position.z}`, config.scene);
      debugMat.diffuseColor = new BABYLON.Color3(0, 0, 1); // Blue for exit door
      debugMat.alpha = 0.5;
      this.collisionBox.material = debugMat;
    }
    // Exit door instantiated at position with tile row/col; consider emitting e.g. DUNGEON_EXIT_SPAWNED here.

    // Set up pointer observable to handle clicks on this specific door
    this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== BABYLON.PointerEventTypes.POINTERDOWN) return;

      const evt = pointerInfo.event;
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (!canvas || !evt) return;

      // Get click coordinates
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (evt.clientX - rect.left) * scaleX;
      const y = (evt.clientY - rect.top) * scaleY;

      // Pick to see what was clicked
      const pickInfo = this.scene.pick(x, y);
      // Optional: emit click/pick event when showDebug or for analytics.

      // Only respond if THIS door's mesh or collision box was clicked
      if (!pickInfo.hit) return;
      if (pickInfo.pickedMesh !== this.mesh && pickInfo.pickedMesh !== this.collisionBox) return;

      // Check if player is facing the door
      if (!this.isPlayerFacing(5)) {
        // Player clicked exit but not facing it; consider emitting e.g. DUNGEON_EXIT_CLICK_NOT_FACING.
        return;
      }
      this.handleClick();
    });
  }

  /**
   * Handle click on exit door
   */
  private handleClick(): void {
    if (this.isOpen) {
      // Exit door already open; consider emitting if needed.
      return;
    }

    // Check if player has the golden key
    if (this.hasGoldenKey()) {
      this.open();
    } else {
      this.eventsManager.emit(DungeonEvents.DOOR_LOCKED);
    }
  }

  /**
   * Check if player is facing this door using raycast
   */
  private isPlayerFacing(maxDistance: number = 5): boolean {
    const ray = this.camera.getForwardRay(maxDistance);

    if (!ray) return false;

    const hit = this.scene.pickWithRay(ray, (mesh) => {
      return mesh === this.mesh || mesh === this.collisionBox;
    });

    return hit?.hit || false;
  }

  /**
   * Opens the exit door
   */
  public open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.eventsManager.emit(DungeonEvents.OPEN_EXIT_DOOR);

    // Disable collision
    this.collisionBox.checkCollisions = false;

    // Change to open door texture
    this.mesh.material = this.openMaterial;

    // No longer interactable; stop listening to pointer events
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }
  }

  /**
   * Check if player has entered the exit door tile
   * Call this from the game loop
   */
  public update(cameraPosition: BABYLON.Vector3): void {
    if (!this.isOpen || this.hasTriggeredEntry) return;

    // Calculate which tile the camera is in
    const cameraTileCol = Math.floor(cameraPosition.x / this.cellSize);
    const cameraTileRow = Math.floor(cameraPosition.z / this.cellSize);

    // Check if player is in the same tile as the exit door
    if (cameraTileCol === this.tileCol && cameraTileRow === this.tileRow) {
      this.hasTriggeredEntry = true;
      // Player entered exit tile; onEnterTile callback runs below. Emit e.g. DUNGEON_EXIT_ENTERED here if needed.

      // Call the callback if provided
      if (this.onEnterTile) {
        this.onEnterTile();
      }
    }
  }

  /**
   * Try to open the exit door (e.g. when player presses Space while facing it).
   * Opens if player has golden key; otherwise emits DOOR_LOCKED.
   */
  public tryOpen(): void {
    if (this.isOpen) return;
    if (this.hasGoldenKey()) {
      this.open();
    } else {
      this.eventsManager.emit(DungeonEvents.DOOR_LOCKED);
    }
  }

  public getIsOpen(): boolean {
    return this.isOpen;
  }

  public getPosition(): BABYLON.Vector3 {
    return this.position;
  }

  public getPickableMeshes(): BABYLON.Mesh[] {
    return [this.mesh, this.collisionBox];
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Remove pointer observer
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    this.mesh.dispose();
    this.collisionBox.dispose();
  }
}